/**
 * Image Cache Utility
 *
 * Tracks which images have been successfully loaded to prevent
 * showing loading states on subsequent navigations.
 *
 * The browser's HTTP cache handles actual image caching.
 * This module just tracks which URLs we've seen to optimize UX.
 */

// Track which images have been successfully loaded
const loadedImages = new Set<string>();

// Track images currently being loaded
const loadingImages = new Map<string, Promise<void>>();

/**
 * Check if an image URL has been loaded before.
 * Use this to skip loading states for known images.
 */
export function isImageCached(url: string | undefined | null): boolean {
  if (!url) return false;
  return loadedImages.has(url);
}

/**
 * Mark an image as successfully loaded.
 * Call this from onLoad handlers.
 */
export function markImageLoaded(url: string | undefined | null): void {
  if (url) {
    loadedImages.add(url);
    loadingImages.delete(url);
  }
}

/**
 * Preload a single image.
 * Returns a promise that resolves when the image is loaded.
 * Rejects if the image fails to load.
 */
export function preloadImage(url: string | undefined | null): Promise<void> {
  if (!url) return Promise.resolve();

  // Already loaded
  if (loadedImages.has(url)) {
    return Promise.resolve();
  }

  // Currently loading
  if (loadingImages.has(url)) {
    return loadingImages.get(url) ?? Promise.resolve();
  }

  // Start loading - don't use crossOrigin to avoid CORS issues with Firebase Storage
  const loadPromise = new Promise<void>((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      loadedImages.add(url);
      loadingImages.delete(url);
      resolve();
    };

    img.onerror = () => {
      loadingImages.delete(url);
      reject(new Error(`Failed to load image: ${url}`));
    };

    // Don't set crossOrigin - Firebase Storage URLs may not have CORS headers
    // and setting crossOrigin causes the request to fail
    img.src = url;
  });

  loadingImages.set(url, loadPromise);
  return loadPromise;
}

/**
 * Preload multiple images in parallel
 */
export function preloadImages(urls: (string | undefined | null)[]): Promise<void[]> {
  const validUrls = urls.filter((url): url is string => !!url);
  return Promise.all(validUrls.map(preloadImage));
}

const IMAGE_FIELDS = [
  'photoURL',
  'imageUrl',
  'thumbnailUrl',
  'url',
  'downloadUrl',
  'coverUrl',
  'avatarUrl',
] as const;

function extractImageFields(obj: Record<string, unknown>): string[] {
  const urls: string[] = [];
  for (const field of IMAGE_FIELDS) {
    if (typeof obj[field] === 'string' && isImageUrl(obj[field] as string)) {
      urls.push(obj[field] as string);
    }
  }
  return urls;
}

function extractFromAttachments(attachments: unknown[]): string[] {
  const urls: string[] = [];
  for (const attachment of attachments) {
    if (attachment && typeof attachment === 'object') {
      const att = attachment as Record<string, unknown>;
      if (att.type === 'image' && typeof att.url === 'string') {
        urls.push(att.url);
      }
    }
  }
  return urls;
}

function extractFromArray(data: unknown[], depth: number, maxDepth: number): string[] {
  const urls: string[] = [];
  if (depth >= maxDepth) return urls;
  for (const item of data) {
    urls.push(...extractImageUrlsInternal(item, depth + 1, maxDepth));
  }
  return urls;
}

function extractFromObject(
  obj: Record<string, unknown>,
  depth: number,
  maxDepth: number
): string[] {
  const urls: string[] = [];

  urls.push(...extractImageFields(obj));

  if (Array.isArray(obj.attachments)) {
    urls.push(...extractFromAttachments(obj.attachments));
  }

  if (Array.isArray(obj.imageUrls)) {
    for (const url of obj.imageUrls) {
      if (typeof url === 'string') urls.push(url);
    }
  }

  if (depth < maxDepth) {
    for (const value of Object.values(obj)) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        urls.push(...extractImageUrlsInternal(value, depth + 1, maxDepth));
      }
    }
  }

  return urls;
}

function extractImageUrlsInternal(data: unknown, depth: number, maxDepth: number): string[] {
  if (!data) return [];
  if (Array.isArray(data)) return extractFromArray(data, depth, maxDepth);
  if (typeof data === 'object')
    return extractFromObject(data as Record<string, unknown>, depth, maxDepth);
  return [];
}

const DEFAULT_MAX_DEPTH = 10;

export function extractImageUrls(data: unknown): string[] {
  return extractImageUrlsInternal(data, 0, DEFAULT_MAX_DEPTH);
}

/**
 * Check if a URL looks like an image URL
 */
function isImageUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;

  const lowercaseUrl = url.toLowerCase();

  // Exclude known non-image file types
  const nonImageExtensions = [
    '.mp3',
    '.mp4',
    '.wav',
    '.ogg',
    '.webm',
    '.m4a',
    '.aac',
    '.flac',
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.csv',
    '.txt',
    '.json',
    '.xml',
    '.zip',
    '.rar',
  ];

  if (nonImageExtensions.some((ext) => lowercaseUrl.includes(ext))) {
    return false;
  }

  // Check for common image extensions
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];

  if (imageExtensions.some((ext) => lowercaseUrl.includes(ext))) {
    return true;
  }

  // Check for Firebase Storage URLs - only if not excluded above
  if (url.includes('firebasestorage.googleapis.com')) {
    return true;
  }

  // Check for common image CDN patterns
  if (url.includes('/images/') || url.includes('/img/') || url.includes('/photos/')) {
    return true;
  }

  return false;
}

/**
 * Preload images from data and return immediately.
 * This runs in the background without blocking.
 */
export function preloadImagesFromData(data: unknown): void {
  const urls = extractImageUrls(data);
  if (urls.length > 0) {
    // Don't await - run in background
    preloadImages(urls);
  }
}

/**
 * Clear the image cache (useful for logout/cleanup)
 */
export function clearImageCache(): void {
  loadedImages.clear();
  loadingImages.clear();
}

/**
 * Get cache stats for debugging
 */
export function getImageCacheStats(): { loaded: number; loading: number } {
  return {
    loaded: loadedImages.size,
    loading: loadingImages.size,
  };
}
